import { useCallback, useState } from 'react'

import { INTEGRATIONS_WITH_DYNAMIC_SCHEMA, INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../../common/integrations'

import { useCreateExternalUserMutation, useLazyGetAllExternalUsersQuery, useSaveExternalUserByIdMutation } from '../../../store/externalUser/externalUser'
import { selectProcessDocumentContact, setProcessDocumentContact } from '../../../store/processDocument/documentUsers'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectProcessDocumentFileInfo } from '../../../store/processDocument/documentInfo'
import { selectSelectedKeys } from '../../../store/meetingSummary/contactUpdate'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectDocumentProspectingBaseFormValues,
  selectDocumentProspectingIntegrationContactExternalId,
  selectDocumentProspectingServiceId,
  setDocumentProspectingBaseFormValues,
  setDocumentProspectingBaseFormValuesLoading,
  setDocumentProspectingDetailsFormProps,
  setDocumentProspectingDetailsFormValues,
  setDocumentProspectingDetailsFormValuesLoading,
  setDocumentProspectingIntegrationAsSuccess,
  setDocumentProspectingIntegrationContact,
  setDocumentProspectingIntegrationContactErrors,
  setDocumentProspectingPage,
} from '../../../store/processDocument/documentProspecting'
import {
  useCreateIntegrationContactMutation,
  useLazyGetIntegrationUsersQuery,
  useUpdateIntegrationUserMutation,
} from '../../../store/integrations/integrations'

import { ExternalUserServiceIntegration, User, UserOwnerType } from '../../../models/User'
import { AIAgentType } from '../../../models/AIAgent'
import {
  SegmentedDocumentProspectingDetailsFormSchemaType,
  SegmentedDocumentProspectingDetailsFormValuesType,
  DocumentProspectingBaseFormExecutionResultType,
  DocumentProspectingDetailsFormProps,
  ProspectingDetailsFormValues,
  DocumentProspectingPageType,
  ProspectingBaseFormValues,
} from '../../../models/DocumentProspecting'

import { filterDataByKeys, processContactUpdateAgentResult, removeEmptyFields } from '../../UpdateExternalContact/utils'
import { DEFAULT_CONTACT_ROLE_ID } from '../../Contacts/AddContactModal'

import { useDocumentSummary } from '../DocumentSummary/hooks'

import { buildName, mapProspectingBaseFormExecutionResult } from './utils'
import { resetDocumentSummary } from '../../../store/processDocument/documentSummary'
import { useSafeTimeout } from '../hooks'

export const useDocumentProspectingContacts = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const contact = useAppSelector(selectProcessDocumentContact)

  const [triggerCreateIntegrationUser] = useCreateIntegrationContactMutation()
  const [triggerUpdateIntegrationUser] = useUpdateIntegrationUserMutation()
  const [triggerGetIntegrationUsers] = useLazyGetIntegrationUsersQuery()
  const [triggerCreateExternalUser] = useCreateExternalUserMutation()
  const [triggerUpdateExternalUser] = useSaveExternalUserByIdMutation()
  const [triggerGetAllExternalUsers] = useLazyGetAllExternalUsersQuery()

  const createContact = useCallback(
    async (baseFormValues: ProspectingBaseFormValues) => {
      try {
        const { firstName, lastName, email } = baseFormValues

        const name = buildName(firstName, lastName)

        const { data: createExternalUserResponse } = await triggerCreateExternalUser({
          accountId,
          userData: {
            firstName,
            lastName,
            email,
            name,
            owner: {
              ownerType: UserOwnerType.USER,
              ownerId: currentUserId,
            },
            roleIds: [DEFAULT_CONTACT_ROLE_ID],
            identity: { type: 'external', location: email },
            referenceUserId: null,
          },
        })
        if (!createExternalUserResponse) {
          return null
        }
        const { data: externalUser } = createExternalUserResponse
        dispatch(setProcessDocumentContact(externalUser))
        return externalUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, currentUserId, dispatch, triggerCreateExternalUser],
  )

  const updateContact = useCallback(
    async (contact: User, integration: ExternalUserServiceIntegration) => {
      try {
        const { id: userId, integrations } = contact
        const { data: updateExternalUserResponse } = await triggerUpdateExternalUser({
          accountId,
          userId,
          userData: {
            integrations: integrations ? [...integrations, integration] : [integration],
            owner: {
              ownerType: UserOwnerType.USER,
              ownerId: currentUserId,
            },
            referenceUserId: null,
          },
        })
        if (!updateExternalUserResponse) {
          return null
        }
        const { data: externalUser } = updateExternalUserResponse
        dispatch(setProcessDocumentContact(externalUser))
        return externalUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, currentUserId, dispatch, triggerUpdateExternalUser],
  )

  const createIntegrationContact = useCallback(
    async (serviceId: IntegrationServiceId, baseFormValues: ProspectingBaseFormValues, detailsFormValues?: ProspectingDetailsFormValues) => {
      try {
        const { firstName, lastName, email } = baseFormValues

        const contact = detailsFormValues?.data ?? {}
        const name = buildName(firstName, lastName)

        const { data: createIntegrationUserResponse } = await triggerCreateIntegrationUser({
          accountId,
          connector: serviceId,
          firstName,
          lastName,
          email,
          name,
          contact,
        })
        if (!createIntegrationUserResponse) {
          return null
        }
        const { data: integrationUser, errors } = createIntegrationUserResponse
        dispatch(setDocumentProspectingIntegrationContact(integrationUser))
        if (errors) {
          dispatch(setDocumentProspectingIntegrationContactErrors(errors))
        }
        return integrationUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerCreateIntegrationUser],
  )

  const updateIntegrationContact = useCallback(
    async (serviceId: IntegrationServiceId, externalId: string, detailsFormValues: ProspectingDetailsFormValues) => {
      try {
        const relatedTo = { id: externalId }
        const { data: payload } = detailsFormValues
        const { data: updateIntegrationUserResponse } = await triggerUpdateIntegrationUser({
          accountId,
          connector: serviceId,
          relatedTo,
          payload,
        })
        if (!updateIntegrationUserResponse) {
          return null
        }
        const { data: integrationUser, errors } = updateIntegrationUserResponse
        dispatch(setDocumentProspectingIntegrationContact(integrationUser))
        if (errors) {
          dispatch(setDocumentProspectingIntegrationContactErrors(errors))
        }
        return integrationUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerUpdateIntegrationUser],
  )

  const createOrUpdateContact = useCallback(
    async (baseFormValues: ProspectingBaseFormValues, integration: ExternalUserServiceIntegration) => {
      if (contact) {
        return updateContact(contact, integration)
      }
      const createdContact = await createContact(baseFormValues)
      if (!createdContact) {
        return null
      }
      return updateContact(createdContact, integration)
    },
    [contact, createContact, updateContact],
  )

  const getContact = useCallback(
    async (email: string) => {
      try {
        const { data: externalUsersResponse } = await triggerGetAllExternalUsers({ accountId, emails: [email] })
        if (!externalUsersResponse) {
          return null
        }
        const { data: externalUsers } = externalUsersResponse
        if (!externalUsers.length) {
          return null
        }

        const externalUser = externalUsers[0]
        dispatch(setProcessDocumentContact(externalUser))

        return externalUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerGetAllExternalUsers],
  )

  const getIntegrationContact = useCallback(
    async (connector: IntegrationServiceId, email: string) => {
      try {
        const { data: integrationUsersResponse } = await triggerGetIntegrationUsers({ accountId, connector, filters: { email } })
        if (!integrationUsersResponse) {
          return null
        }

        const { data: integrationUsers } = integrationUsersResponse
        if (!integrationUsers.length) {
          return null
        }

        const integrationUser = integrationUsers[0]
        dispatch(setDocumentProspectingIntegrationContact(integrationUser))

        return integrationUser
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, dispatch, triggerGetIntegrationUsers],
  )

  return {
    createContact,
    updateContact,
    getContact,
    createIntegrationContact,
    updateIntegrationContact,
    getIntegrationContact,
    createOrUpdateContact,
  }
}

export const useProspectingDetailsForm = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const integrationContactExternalId = useAppSelector(selectDocumentProspectingIntegrationContactExternalId)
  const baseFormValues = useAppSelector(selectDocumentProspectingBaseFormValues)

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()

  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const { createOrUpdateContact, createIntegrationContact, updateIntegrationContact } = useDocumentProspectingContacts()

  const executeDetailsFormValueGenerationAiAgent = useCallback(
    async (serviceId: IntegrationServiceId, fileId: string, schema: Record<string, unknown>, previousValues: Record<string, unknown>) => {
      try {
        const aiAgentResult = await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_PROSPECTING_DETAILS_FORM_VALUE_GENERATION,
            context: {
              previousValues,
              schema,
            },
            attachments: [{ fileId }],
          },
        }).unwrap()
        if (!aiAgentResult) {
          return null
        }
        return processContactUpdateAgentResult(serviceId, schema, previousValues, aiAgentResult)
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerExecuteAiAgent],
  )

  const generateSegmentedDetailsFormValues = useCallback(
    async (
      serviceId: IntegrationServiceId,
      fileId: string,
      schema: SegmentedDocumentProspectingDetailsFormSchemaType,
      previousValues: SegmentedDocumentProspectingDetailsFormValuesType,
    ) => {
      const keys = Object.keys(schema)

      const detailsFormValues = await Promise.all(
        keys.map((key) => executeDetailsFormValueGenerationAiAgent(serviceId, fileId, schema[key].schema, previousValues[key].values)),
      )

      return Object.fromEntries(keys.map((key, index) => [key, detailsFormValues[index]]))
    },
    [executeDetailsFormValueGenerationAiAgent],
  )

  const generateDetailsFormValntegration = useCallback(
    async (serviceId: IntegrationServiceId, fileId: string, props: DocumentProspectingDetailsFormProps) => {
      if (props.type === 'segmented') {
        return generateSegmentedDetailsFormValues(serviceId, fileId, props.schema, props.previousValues)
      }
      return executeDetailsFormValueGenerationAiAgent(serviceId, fileId, props.schema, props.previousValues)
    },
    [executeDetailsFormValueGenerationAiAgent, generateSegmentedDetailsFormValues],
  )

  const generateFormValues = useCallback(
    async (serviceId: IntegrationServiceId, fileId: string, props: DocumentProspectingDetailsFormProps) => {
      dispatch(setDocumentProspectingDetailsFormValuesLoading(true))
      try {
        const detailsFormValues = await generateDetailsFormValntegration(serviceId, fileId, props)
        dispatch(setDocumentProspectingDetailsFormValues({ type: props.type, data: detailsFormValues }))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentProspectingDetailsFormValuesLoading(false))
      }
    },
    [generateDetailsFormValntegration, dispatch],
  )

  const handleDetailsFormSubmitForIntegration = useCallback(
    async (serviceId: IntegrationServiceId, baseFormValues: ProspectingBaseFormValues, detailsFormValues: ProspectingDetailsFormValues) => {
      if (INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)) {
        if (!integrationContactExternalId) {
          return null
        }
        return updateIntegrationContact(serviceId, integrationContactExternalId, detailsFormValues)
      }

      const integrationContact = await createIntegrationContact(serviceId, baseFormValues, detailsFormValues)
      if (!integrationContact) {
        return null
      }

      const integration = integrationContact.integrations?.find(({ serviceId }) => serviceId === serviceId)
      if (!integration) {
        return null
      }

      await createOrUpdateContact(baseFormValues, integration)

      return integrationContact
    },
    [createIntegrationContact, createOrUpdateContact, integrationContactExternalId, updateIntegrationContact],
  )

  const submitFormValues = useCallback(
    async (detailsFormValues: ProspectingDetailsFormValues) => {
      if (!serviceId || !baseFormValues) {
        return false
      }

      const filteredDetailsFormValues = removeEmptyFields(serviceId, filterDataByKeys(detailsFormValues, selectedKeys)) as ProspectingDetailsFormValues
      if (!Object.keys(filteredDetailsFormValues)) {
        return false
      }

      setSubmitLoading(true)
      try {
        const integrationContact = await handleDetailsFormSubmitForIntegration(serviceId, baseFormValues, filteredDetailsFormValues)
        if (!integrationContact) {
          return false
        }
        dispatch(setDocumentProspectingIntegrationAsSuccess(serviceId))
        return true
      } finally {
        setSubmitLoading(false)
      }
    },
    [baseFormValues, dispatch, handleDetailsFormSubmitForIntegration, selectedKeys, serviceId],
  )

  return { submitLoading, submitFormValues, generateFormValues }
}

export const useProspectingBaseForm = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()

  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const { getContact, getIntegrationContact, createIntegrationContact, createOrUpdateContact } = useDocumentProspectingContacts()

  const generateFormValues = useCallback(
    async (fileId: string) => {
      dispatch(setDocumentProspectingBaseFormValuesLoading(true))
      try {
        const { data: aiAgentResult } = await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_PROSPECTING_BASE_FORM_VALUE_GENERATION,
            context: {},
            attachments: [{ fileId }],
          },
        })
        if (!aiAgentResult) {
          return null
        }
        const { result } = aiAgentResult as DocumentProspectingBaseFormExecutionResultType
        const baseFormValues = mapProspectingBaseFormExecutionResult(result)
        dispatch(setDocumentProspectingBaseFormValues(baseFormValues))
        return baseFormValues
      } catch (e) {
        console.error(e)
        return null
      } finally {
        dispatch(setDocumentProspectingBaseFormValuesLoading(false))
      }
    },
    [accountId, dispatch, triggerExecuteAiAgent],
  )

  const submitFormValues = useCallback(
    async (baseFormValues: ProspectingBaseFormValues) => {
      dispatch(setDocumentProspectingBaseFormValues(baseFormValues))

      if (!serviceId) {
        return false
      }

      setSubmitLoading(true)
      try {
        const { email } = baseFormValues
        await getContact(email)

        const existingIntegrationContact = await getIntegrationContact(serviceId, email)
        if (existingIntegrationContact) {
          return false
        }

        if (!INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)) {
          return true
        }

        const integrationContact = await createIntegrationContact(serviceId, baseFormValues)
        if (!integrationContact) {
          return false
        }

        const integration = integrationContact.integrations?.find(({ serviceId }) => serviceId === serviceId)
        if (!integration) {
          return false
        }

        await createOrUpdateContact(baseFormValues, integration)

        const externalInfo = integrationContact.externalInfos.find(({ serviceId }) => serviceId === serviceId)
        if (!externalInfo) {
          return false
        }

        const { contactSchema: schema, contactValues: previousValues } = externalInfo
        const propsType = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId) ? 'segmented' : 'default'
        dispatch(setDocumentProspectingDetailsFormProps({ type: propsType, schema, previousValues }))

        return true
      } finally {
        setSubmitLoading(false)
      }
    },
    [createIntegrationContact, createOrUpdateContact, dispatch, getContact, getIntegrationContact, serviceId],
  )

  return { submitLoading, submitFormValues, generateFormValues }
}

export const useShowSummaryForDocumentProspecting = () => {
  const dispatch = useAppDispatch()

  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)

  const { calculateDocumentType, summarizeDocument } = useDocumentSummary()
  const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout()

  const resetSummary = useCallback(() => {
    dispatch(setDocumentProspectingPage(DocumentProspectingPageType.PROSPECTING_STEPS))
    dispatch(resetDocumentSummary())
  }, [dispatch])

  const showSummary = useCallback(async () => {
    if (!fileInfo) {
      return
    }

    clearSafeTimeout()

    dispatch(setDocumentProspectingPage(DocumentProspectingPageType.SUMMARY_FILE_UPLOAD))

    const documentType = await calculateDocumentType(fileInfo)
    if (!documentType) {
      resetSummary()
      return
    }

    const documentSummary = await summarizeDocument(documentType, fileInfo)
    if (!documentSummary) {
      resetSummary()
      return
    }

    setSafeTimeout(() => dispatch(setDocumentProspectingPage(DocumentProspectingPageType.SUMMARY)), 500)
  }, [calculateDocumentType, clearSafeTimeout, dispatch, fileInfo, resetSummary, setSafeTimeout, summarizeDocument])

  return { showSummary }
}
